import {
  NotificationProps,
  NotificationOptions,
  NotificationColor,
  NotificationGroup,
  NotificationConfig,
  MessagePosition,
} from '@/shared/types/message';
import { SizeAlert } from '@/shared/types/alert';

type NotificationInput = string | (NotificationOptions & { message?: string });

// Listener para notificaciones agrupadas
type NotificationGroupListener = (
  groups: Record<NotificationColor, NotificationGroup | null>
) => void;

// Configuración por defecto
const defaultNotificationConfig: NotificationConfig = {
  position: 'top-right',
  duration: 5000,
  animationDuration: 300,
  size: 'sm',
  showTimer: true,
};

const notificationConfig: NotificationConfig = { ...defaultNotificationConfig };

/**
 * Servicio de Notificaciones
 *
 * Comportamiento:
 * - Las notificaciones se agrupan por color (brand | neutral)
 * - Solo se muestra UNA notificación por grupo (la primera)
 * - Se mantiene un contador de notificaciones acumuladas
 * - El contador solo se muestra cuando > 1
 * - Cada nueva notificación reinicia el timer
 * - Al cerrar, se descartan todas las notificaciones del grupo
 */
class NotificationService {
  private listeners: NotificationGroupListener[] = [];
  private idCounter = 0;

  // Grupos de notificaciones por color
  private groups: Record<NotificationColor, NotificationGroup | null> = {
    brand: null,
    neutral: null,
  };

  // Timers para cada grupo
  private timers: Record<NotificationColor, NodeJS.Timeout | null> = {
    brand: null,
    neutral: null,
  };

  // Intervalos para actualizar el progreso del timer
  private progressIntervals: Record<NotificationColor, NodeJS.Timeout | null> = {
    brand: null,
    neutral: null,
  };

  // Timers para la eliminación después de animación
  private removeTimers: Record<NotificationColor, NodeJS.Timeout | null> = {
    brand: null,
    neutral: null,
  };

  /**
   * Muestra una notificación
   */
  show(
    messageOrOptions: NotificationInput,
    color: NotificationColor = 'brand',
    options: NotificationOptions = {},
  ): number {
    const id = Date.now() + ++this.idCounter;

    // Parsear opciones
    let message: string;
    let title: string | undefined;
    let finalOptions: NotificationOptions;

    if (typeof messageOrOptions === 'string') {
      message = '';
      title = messageOrOptions;
      finalOptions = { ...options };
    } else {
      message = messageOrOptions.message ?? '';
      title = messageOrOptions.title;
      finalOptions = { ...options, ...messageOrOptions };
    }

    // Crear la notificación
    const notification: NotificationProps = {
      id,
      message,
      title,
      subtitle: finalOptions.subtitle,
      description: finalOptions.description,
      color,
      avatar: finalOptions.avatar,
      icon: finalOptions.icon,
      position: finalOptions.position ?? notificationConfig.position,
      duration: finalOptions.duration ?? notificationConfig.duration,
      showCloseButton: finalOptions.showCloseButton ?? true,
      isLeaving: false,
      showTimer: notificationConfig.showTimer,
      onClick: finalOptions.onClick,
      onClose: finalOptions.onClose,
      onPrimaryClick: finalOptions.onPrimaryClick,
      onSecondaryClick: finalOptions.onSecondaryClick,
      primaryButtonText: finalOptions.primaryButtonText,
      secondaryButtonText: finalOptions.secondaryButtonText,
      size: (finalOptions.size ??
        (notificationConfig.size === 'lg' ? 'md' : notificationConfig.size)) as SizeAlert,
      // Añadir variant y animation para que sea igual al Toast
      // Usar 'contained' como default igual que Toast (messageConfig.variant)
      variant: finalOptions.variant ?? 'outlined',
      animation: finalOptions.animation ?? 'fade',
    };

    // Validación en desarrollo
    if (process.env.NODE_ENV !== 'production') {
      if (
        notification.duration !== undefined &&
        (isNaN(notification.duration) ||
          !isFinite(notification.duration) ||
          notification.duration < 0)
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          `[NotificationService] Invalid duration "${notification.duration}". Using default.`,
        );
        notification.duration = notificationConfig.duration;
      }
      if (
        notification.position &&
        ![
          'top-left',
          'top-center',
          'top-right',
          'center-left',
          'center',
          'center-right',
          'bottom-left',
          'bottom-center',
          'bottom-right',
        ].includes(notification.position)
      ) {
        // eslint-disable-next-line no-console
        console.warn(
          `[NotificationService] Invalid position "${notification.position}". Using default.`,
        );
        notification.position = notificationConfig.position;
      }
      if (!['brand', 'neutral'].includes(color)) {
        // eslint-disable-next-line no-console
        console.warn(`[NotificationService] Invalid color "${color}".`);
      }
    }

    // Agregar al grupo correspondiente
    this.addToGroup(color, notification);

    return id;
  }

  /**
   * Agrega una notificación al grupo correspondiente
   */
  private addToGroup(color: NotificationColor, notification: NotificationProps) {
    const existingGroup = this.groups[color];

    if (existingGroup) {
      // Ya existe un grupo, agregar a la cola e incrementar contador
      existingGroup.queue.push(notification);
      existingGroup.count = existingGroup.queue.length;

      // Reiniciar el timer
      this.resetTimer(color);
    } else {
      // Crear nuevo grupo
      this.groups[color] = {
        color,
        notification,
        queue: [notification],
        count: 1,
        timerProgress: 100,
        isLeaving: false,
      };

      // Iniciar timer
      this.startTimer(color);
    }

    // Notificar a los listeners
    this.notifyListeners();
  }

  /**
   * Inicia el timer para un grupo
   */
  private startTimer(color: NotificationColor) {
    const group = this.groups[color];
    if (!group) return;

    const duration = group.notification.duration ?? notificationConfig.duration;
    if (duration <= 0) return; // No auto-cerrar si duration es 0

    // Limpiar timer anterior si existe
    this.clearTimer(color);

    // Iniciar el progreso del timer
    const startTime = Date.now();
    group.timerProgress = 100;

    // Actualizar progreso cada 50ms
    this.progressIntervals[color] = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progress = Math.max(0, 100 - (elapsed / duration) * 100);

      if (this.groups[color]) {
        this.groups[color]!.timerProgress = progress;
        this.notifyListeners();
      }
    }, 50);

    // Timer para auto-cerrar
    this.timers[color] = setTimeout(() => {
      this.dismissGroup(color);
    }, duration);
  }

  /**
   * Reinicia el timer para un grupo
   */
  private resetTimer(color: NotificationColor) {
    this.clearTimer(color);
    this.startTimer(color);
  }

  /**
   * Limpia el timer de un grupo
   */
  private clearTimer(color: NotificationColor) {
    if (this.timers[color]) {
      clearTimeout(this.timers[color]!);
      this.timers[color] = null;
    }
    if (this.progressIntervals[color]) {
      clearInterval(this.progressIntervals[color]!);
      this.progressIntervals[color] = null;
    }
    if (this.removeTimers[color]) {
      clearTimeout(this.removeTimers[color]!);
      this.removeTimers[color] = null;
    }
  }

  /**
   * Cierra todas las notificaciones de un grupo
   */
  dismissGroup(color: NotificationColor) {
    const group = this.groups[color];
    if (!group) return;

    // Marcar como saliendo para animación
    group.isLeaving = true;
    this.notifyListeners();

    // Limpiar timer
    this.clearTimer(color);

    // Eliminar después de la animación
    this.removeTimers[color] = setTimeout(() => {
      this.groups[color] = null;
      this.removeTimers[color] = null;
      this.notifyListeners();
    }, notificationConfig.animationDuration);
  }

  /**
   * Cierra una notificación específica por ID
   * (En el sistema de notificaciones, esto cierra todo el grupo)
   */
  dismiss(id: number) {
    // Buscar en qué grupo está la notificación
    for (const color of ['brand', 'neutral'] as NotificationColor[]) {
      const group = this.groups[color];
      if (group && group.queue.some((n) => n.id === id)) {
        this.dismissGroup(color);
        return;
      }
    }
  }

  /**
   * Notificación de marca (brand)
   */
  brand(messageOrOptions: NotificationInput, options?: NotificationOptions): number {
    return this.show(messageOrOptions, 'brand', options);
  }

  /**
   * Notificación neutral
   */
  neutral(messageOrOptions: NotificationInput, options?: NotificationOptions): number {
    return this.show(messageOrOptions, 'neutral', options);
  }

  /**
   * Configura las opciones del servicio
   */
  configure(options: Partial<NotificationConfig>) {
    Object.assign(notificationConfig, options);
  }

  /**
   * Obtiene los grupos actuales
   */
  getGroups(): Record<NotificationColor, NotificationGroup | null> {
    return { ...this.groups };
  }

  /**
   * Suscribirse a cambios
   */
  subscribe(listener: NotificationGroupListener): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notifica a todos los listeners
   */
  private notifyListeners() {
    const groups = this.getGroups();
    this.listeners.forEach((listener) => listener(groups));
  }

  /**
   * Obtiene la configuración actual
   */
  getConfig(): NotificationConfig {
    return { ...notificationConfig };
  }
}

// Instancia singleton
export const notificationService = new NotificationService();

// Hook para acceder al servicio
export const useNotificationService = () => {
  return notificationService;
};
